import React, { useEffect, useRef } from 'react';

import { App } from '@flipdish/api-client-typescript';
import { useSnackbar } from 'notistack';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';

import {
  createVoucher,
  createVoucherRequestReset,
  vouchersActions,
} from '../../actions/vouchers.actions';
import { getSelectedApp } from '../../selectors/app.selector';
import PageLayout from '../../ui/Layout';
import PaperContainer from '../../ui/Layout/PaperContainer';
import PercentageDiscountForm from './components/PercentageDiscountForm';
import { getIsCreateLoading } from './selectors';

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> & { dispatch: ThunkDispatch };
const PercentageDiscountVoucher = (props: Props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    return () => {
      props.reset();
    };
  }, []);

  const snackbarRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (props.isSaving && !snackbarRef.current) {
      snackbarRef.current = enqueueSnackbar('Saving', {
        variant: 'default',
        persist: true,
      });
    }
    return () => {
      if (snackbarRef.current) {
        closeSnackbar(snackbarRef.current);
      }
    };
  }, [props.isSaving]);
  useEffect(() => {
    if (props.isSuccess) {
      enqueueSnackbar('Success', {
        variant: 'success',
      });
    }
  }, [props.isSuccess]);
  useEffect(() => {
    if (props.errorMessage) {
      const msg =
        props.translate(props.errorMessage, undefined, {
          missingTranslationMsg: undefined,
        }) || props.translate('Error_please_try_again_later');
      enqueueSnackbar(msg, {
        variant: 'error',
      });
    }
  }, [props.errorMessage]);

  useEffect(() => {
    if (!props.voucher && props.match.params.voucherId) {
      props.get(props.match.params.voucherId);
      if (props.extraStats) {
        props.getStats(props.match.params.voucherId);
      }
    }
  }, [props.voucher]);

  if (props.isSuccess && props.vouchersRoute) {
    return <Redirect to={props.vouchersRoute} />;
  }

  const { create, update, voucher, match, isLoading, currentApp } = props;
  const editMode = match.params.voucherId ? true : false;

  return (
    <PageLayout
      toParent={`/${currentApp.AppId}/vouchers`}
      documentTitle="Percentage_discount"
      title={<Translate id="Percentage_discount" />}
    >
      <PaperContainer fluid>
        <PercentageDiscountForm
          key={editMode && voucher ? voucher.VoucherId : 'form'}
          update={update}
          create={create}
          editMode={editMode}
          serverErrorMessage={props.errorMessage}
          voucher={voucher}
          isLoading={isLoading}
        />
      </PaperContainer>
    </PageLayout>
  );
};

const mapStateToProps = (state: AppState, ownProps) => {
  const { vouchers, currentApp, permissions } = state;
  const isSaving = getIsCreateLoading(state);

  const isSuccess = vouchers.addNew.request.success === true;
  const error = vouchers.addNew.request.error;
  const errorMessage = error ? error.message : undefined;

  const vouchersRoute = currentApp.AppId ? `/${currentApp.AppId}/vouchers` : undefined;
  const voucher = ownProps.match.params.voucherId ? vouchers.voucher : null;
  const extraStats = permissions.some(
    (p) => p === App.AppResourceSetEnum.ViewVouchersStatistics.toString()
  );

  return {
    isSaving,
    isSuccess,
    errorMessage,
    vouchersRoute,
    translate: getTranslate(state.locale),
    voucher,
    match: ownProps.match,
    extraStats,
    currentApp: getSelectedApp(state),
    isLoading: vouchers.voucherLoading,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, ownProps) => {
  return {
    create: (voucher) => {
      return dispatch(createVoucher(voucher, true));
    },
    reset: () => {
      dispatch(createVoucherRequestReset());
    },
    get: (voucherId) => {
      dispatch(vouchersActions.getVoucher(voucherId));
    },
    getStats: (voucherId) => {
      dispatch(vouchersActions.getVoucherStatistics(voucherId));
    },
    update: (voucher) => {
      return dispatch(
        vouchersActions.updateVoucher(
          {
            voucherId: parseInt(ownProps.match.params.voucherId),
            voucher,
            percentValue: voucher.PercentDiscountDetails.PercentageDiscount,
          },
          true
        )
      );
    },
  };
};

const EnhancedComponent = connect(mapStateToProps, mapDispatchToProps)(PercentageDiscountVoucher);

export default EnhancedComponent;
